import React, { useState } from 'react';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { mergeEmrSettings } from '../../config/emrSettings';
import { HMS_MODULES } from '../../config/modules';
import { STAFF_ROLES } from '../../config/plans';
import { ROLES } from '../../permissions';

/** Per-role module access control */
export default function AccessControlPanel() {
  const { data, loading, reload } = useLiveData(() => ipApi.getEmrSettings(), []);
  const settings = mergeEmrSettings(data?.settings);
  const [access, setAccess] = useState(settings.roleModuleAccess || {});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  React.useEffect(() => {
    if (data) setAccess(mergeEmrSettings(data.settings).roleModuleAccess || {});
  }, [data]);

  const roles = STAFF_ROLES.filter((r) => r.id !== 'manager');

  const toggle = (roleId, moduleId) => {
    setAccess((prev) => {
      const disabled = new Set(prev[roleId]?.disabledModules || []);
      if (disabled.has(moduleId)) disabled.delete(moduleId);
      else disabled.add(moduleId);
      return { ...prev, [roleId]: { disabledModules: [...disabled] } };
    });
  };

  const isDisabled = (roleId, moduleId) => (access[roleId]?.disabledModules || []).includes(moduleId);

  const save = async () => {
    setBusy(true);
    setMsg('');
    try {
      await ipApi.patchEmrSettings({ settings: { ...settings, roleModuleAccess: access } });
      await reload();
      setMsg('Access control saved. Staff may need to re-login to see changes.');
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mb">
      <div className="card-head-row">
        <div>
          <h3 style={{ margin: 0 }}>Access control</h3>
          <p className="hint" style={{ margin: '4px 0 0', fontSize: 12 }}>Disable HMS modules per role. Manager always has full access.</p>
        </div>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy || loading} onClick={save}>{busy ? 'Saving…' : 'Save access'}</button>
      </div>
      {msg && <p className="hint" style={{ color: 'var(--color-primary-dark)' }}>{msg}</p>}
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Module</th>
              {roles.map((r) => <th key={r.id}>{r.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {HMS_MODULES.map((m) => (
              <tr key={m.id}>
                <td>{m.label}</td>
                {roles.map((r) => (
                  <td key={r.id} style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={!isDisabled(r.id, m.id)} onChange={() => toggle(r.id, m.id)} title={`${m.label} for ${r.label}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="hint" style={{ fontSize: 11, marginTop: 8 }}>Unchecked = module hidden for that role. Core routes (Q Page, EMR) remain via {ROLES.manager.label} setup.</p>
    </div>
  );
}
